import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import Select from "react-select";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import {
  getMeetingRoomUsageForOffice,
  MeetingRoomUsageLine,
  MeetingRoomUsageReport
} from '@features/reporting/services/MeetingRoomUsageReportService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { Moment } from "moment";
import moment from "moment";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { CustomToolTip } from "../components/CustomToolTip";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';

const COMPANY_WIDE_ID = -1;

type RoomSortColumn = 'roomName' | 'totalBookings' | 'totalHoursBooked' | 'averageBookingHours' | 'utilizationPercentage';

export function MeetingRoomUsageReportingPage() {
  const {t} = useTranslation();
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);

  const officeOptions = useMemo(() => {
    const companyWide = { value: COMPANY_WIDE_ID, label: t('reports-page.meeting-room-usage-company-wide') };
    const officeItems = rootOffices.map((o: OfficeEntity) => ({ value: o.id, label: o.label }));
    return [companyWide, ...officeItems];
  }, [rootOffices, t]);

  const [selectedOption, setSelectedOption] = useState<{ value: number; label: string } | null>(null);
  const office = useMemo(() => {
    if (!selectedOption || selectedOption.value === COMPANY_WIDE_ID) return undefined;
    return rootOffices.find((o: OfficeEntity) => o.id === selectedOption.value);
  }, [selectedOption, rootOffices]);
  const [fromDate, setFromDate] = useState<Moment>(moment().startOf('month'));
  const [toDate, setToDate] = useState<Moment>(moment().endOf('month'));
  const [reportData, setReportData] = useState<MeetingRoomUsageReport | undefined>();
  const [loading, setLoading] = useState(false);
  const [roomSortBy, setRoomSortBy] = useState<RoomSortColumn>('totalHoursBooked');
  const [roomSortDirection, setRoomSortDirection] = useState<'asc' | 'desc'>('desc');

  const isCompanyWide = selectedOption?.value === COMPANY_WIDE_ID;

  useEffect(() => {
    if (!selectedOption && officeOptions.length > 0) {
      setSelectedOption(officeOptions[0]);
    }
  }, [officeOptions, selectedOption]);

  useEffect(() => {
    if (selectedOption && fromDate && toDate) {
      setLoading(true);
      setReportData(undefined);
      getMeetingRoomUsageForOffice(isCompanyWide ? undefined : office, fromDate, toDate)
        .then(setReportData)
        .finally(() => setLoading(false));
    }
  }, [selectedOption, fromDate, toDate, isCompanyWide, office]);

  const onDatesSelected = (from?: Moment, to?: Moment) => {
    if (from) setFromDate(from);
    if (to) setToDate(to);
  };

  const dailyChartData = useMemo(() => {
    if (!reportData?.dailyBreakdown) return [];
    return reportData.dailyBreakdown.map(day => ({
      name: moment(day.date).format('DD MMM'),
      bookings: day.bookings,
      hours: Math.round(day.hoursBooked * 10) / 10,
      toolTipTitle: moment(day.date).format('ddd DD MMM YYYY'),
    }));
  }, [reportData]);

  const roomChartData = useMemo(() => {
    if (!reportData) return [];
    return reportData.rooms
      .filter(r => r.totalBookings > 0)
      .map(r => ({
        name: r.roomName,
        utilization: r.utilizationPercentage,
        bookings: r.totalBookings,
        hours: Math.round(r.totalHoursBooked * 10) / 10,
        toolTipTitle: r.roomName,
      }));
  }, [reportData]);

  const toggleRoomSort = useCallback((column: RoomSortColumn) => {
    if (roomSortBy === column) {
      setRoomSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setRoomSortBy(column);
      setRoomSortDirection(column === 'roomName' ? 'asc' : 'desc');
    }
  }, [roomSortBy]);

  const sortedRooms = useMemo(() => {
    if (!reportData?.rooms) return [];
    const data = [...reportData.rooms];
    data.sort((a, b) => {
      const aVal = a[roomSortBy];
      const bVal = b[roomSortBy];
      if (aVal < bVal) return roomSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return roomSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [reportData, roomSortBy, roomSortDirection]);

  const formatHours = (hours: number) => `${Math.round(hours * 10) / 10}h`;

  const sortIndicator = (column: RoomSortColumn) => (
    <SortIndicator active={roomSortBy === column}>
      {roomSortBy === column ? (roomSortDirection === 'asc' ? ' ▲' : ' ▼') : ' ▲'}
    </SortIndicator>
  );

  return (
    <ReportingContainer>
      <GoBackButton url={"/manage/reporting"} />
      <HeadlineLarge style={{marginBottom: 8}}>{t('reports-page.meeting-room-usage-report-title')}</HeadlineLarge>
      <BodySmall colour={Colours.darkGrey} style={{marginBottom: 16}}>{t('reports-page.meeting-room-usage-disclaimer')}</BodySmall>

      <ControlsRow>
        <ControlGroup>
          <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.meeting-room-usage-office')}</BodyRegular>
          <Select options={officeOptions}
                  value={selectedOption}
                  onChange={(item: any) => setSelectedOption(item)}
                  styles={REACT_SELECT_DROPDOWN_STYLES}
                  isDisabled={loading}
                  isSearchable={false} />
        </ControlGroup>
        <DateRangeGroup>
          <DateRangePicker disabled={loading}
                           onDatesSelected={onDatesSelected}
                           fromDate={fromDate}
                           toDate={toDate} />
        </DateRangeGroup>
      </ControlsRow>

      {loading && (
        <Center>
          <LoadingSpinner hideBorder={true} />
        </Center>
      )}

      {!loading && (!reportData || reportData.rooms.length === 0) && selectedOption && (
        <Center style={{marginTop: 32}}>
          <MountedContentCard>
            <MountedContentCardContent>
              <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.meeting-room-usage-no-data')}</BodyLarge>
              <BodyRegular>{t('reports-page.meeting-room-usage-no-data-description')}</BodyRegular>
            </MountedContentCardContent>
          </MountedContentCard>
        </Center>
      )}

      {!loading && reportData && reportData.rooms.length > 0 && (
        <>
          <SummaryCard>
            <SummaryTitle>{reportData.officeLabel}</SummaryTitle>
            <SummaryGrid>
              <SummaryItem>
                <SummaryValue>{reportData.rooms.length}</SummaryValue>
                <SummaryLabel>{t('reports-page.meeting-room-usage-rooms')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{reportData.totalBookings}</SummaryValue>
                <SummaryLabel>{t('reports-page.meeting-room-usage-total-bookings')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{formatHours(reportData.totalHoursBooked)}</SummaryValue>
                <SummaryLabel>{t('reports-page.meeting-room-usage-hours-booked')}</SummaryLabel>
              </SummaryItem>
              <SummaryItem>
                <SummaryValue>{reportData.utilizationPercentage}%</SummaryValue>
                <SummaryLabel>{t('reports-page.meeting-room-usage-utilization')}</SummaryLabel>
              </SummaryItem>
            </SummaryGrid>
          </SummaryCard>

          {dailyChartData.length > 0 && (
            <FlatContentCard style={{marginBottom: 24}}>
              <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.meeting-room-usage-daily-bookings')}</BodyLarge>
              <div style={{height: 350}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyChartData}
                             margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                    <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                    <XAxis dataKey="name"
                           fontFamily={'"soleil", sans-serif'}
                           fill={Colours.darkGrey}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false} />
                    <YAxis fontFamily={'"soleil", sans-serif'}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false}
                           allowDecimals={false} />
                    <Tooltip content={<CustomToolTip />} />
                    <Line type="monotone"
                          dataKey="bookings"
                          stroke={Colours.green}
                          strokeWidth={2}
                          dot={{fill: Colours.green, r: 3}}
                          activeDot={{r: 5}} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </FlatContentCard>
          )}

          {roomChartData.length > 0 && (
            <FlatContentCard>
              <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.meeting-room-usage-by-room')}</BodyLarge>
              <div style={{height: 350}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={roomChartData}
                            margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                    <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                    <XAxis dataKey="name"
                           fontFamily={'"soleil", sans-serif'}
                           fill={Colours.darkGrey}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false} />
                    <YAxis fontFamily={'"soleil", sans-serif'}
                           fontSize={12}
                           axisLine={false}
                           tickLine={false}
                           domain={[0, 100]}
                           tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={<CustomToolTip />} />
                    <Bar dataKey="utilization" fill={Colours.blue} radius={[4, 4, 0, 0]}>
                      {roomChartData.map((entry, index) => (
                        <Cell key={index} fill={Colours.green} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </FlatContentCard>
          )}

          <RoomSection>
            <BodyLarge weight={600} style={{marginBottom: 12}}>{t('reports-page.meeting-room-usage-room-breakdown')}</BodyLarge>
            <Table>
              <thead>
                <tr>
                  <SortableTh onClick={() => toggleRoomSort('roomName')}>
                    {t('reports-page.meeting-room-usage-room')}
                    {sortIndicator('roomName')}
                  </SortableTh>
                  <Th>{t('reports-page.meeting-room-usage-office')}</Th>
                  <Th>{t('reports-page.meeting-room-usage-capacity')}</Th>
                  <SortableTh onClick={() => toggleRoomSort('totalBookings')}>
                    {t('reports-page.meeting-room-usage-bookings')}
                    {sortIndicator('totalBookings')}
                  </SortableTh>
                  <SortableTh onClick={() => toggleRoomSort('totalHoursBooked')}>
                    {t('reports-page.meeting-room-usage-hours-booked')}
                    {sortIndicator('totalHoursBooked')}
                  </SortableTh>
                  <SortableTh onClick={() => toggleRoomSort('averageBookingHours')}>
                    {t('reports-page.meeting-room-usage-avg-booking')}
                    {sortIndicator('averageBookingHours')}
                  </SortableTh>
                  <SortableTh onClick={() => toggleRoomSort('utilizationPercentage')}>
                    {t('reports-page.meeting-room-usage-utilization')}
                    {sortIndicator('utilizationPercentage')}
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedRooms.map((room: MeetingRoomUsageLine) => (
                  <RoomRow key={room.roomId}>
                    <Td style={{fontWeight: 600}}>{room.roomName}</Td>
                    <Td>{room.officeLabel ?? '-'}</Td>
                    <Td>{room.capacity}</Td>
                    <Td>{room.totalBookings}</Td>
                    <Td>{formatHours(room.totalHoursBooked)}</Td>
                    <Td>{room.totalBookings > 0 ? formatHours(room.averageBookingHours) : '-'}</Td>
                    <Td>{room.utilizationPercentage}%</Td>
                  </RoomRow>
                ))}
              </tbody>
            </Table>
          </RoomSection>
        </>
      )}
    </ReportingContainer>
  );
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const DateRangeGroup = styled.div`
  width: 100%;
  .textField {
    margin-bottom: 0 !important;
  }
  @media (${tablet}) {
    max-width: 400px;
  }
`

const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const RoomSection = styled.div`
  margin-top: 32px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const Th = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
`

const RoomRow = styled.tr`
  &:hover {
    background-color: ${Colours.blue10};
  }
`

const SortableTh = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  cursor: pointer;
  user-select: none;
  &:hover {
    color: ${Colours.blue};
  }
`

const SortIndicator = styled.span<{ active: boolean }>`
  font-size: 10px;
  opacity: ${props => props.active ? 1 : 0.3};
`
